import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFileDownload from '../../../../hooks/useFiledDownload';
import styles from '../Group.module.scss';

const { Box, DropDown, Title, Button, Table } = vp;

type BulkTransactionDetailProps = {
  transactionData: {
    [key: string]: string | number | null | undefined;
  }[];
  backToPrev: () => void;
};

type FC = FunctionComponent<BulkTransactionDetailProps>;

const BulkTransactionDetails: FC = (props: BulkTransactionDetailProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts.FileUpload.BulkTransactionDetails' });
  const { transactionData, backToPrev } = props;
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);
  const [isExportingDOC, setIsExportingDOC] = useState(false);
  const [isExportingText, setIsExportingText] = useState(false);
  const [isExportingJSON, setIsExportingJSON] = useState(false);
  const [isExportingXML, setIsExportingXML] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const {
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
    downloadPdfTable,
  } = useFileDownload();

  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfTable('bulk-transaction-overview', 'bulk-transaction');
    }

    if (isExportingCSV) {
      downloadCsvFile('bulk-transaction-overview', 'bulk-transaction');
    }

    if (isExportingXLS) {
      downloadXls('bulkTransactionTable', 'bulk-transaction');
    }

    if (isExportingDOC) {
      downloadDoc('bulkTransactionTable', 'bulk-transaction');
    }

    if (isExportingText) {
      downloadTxt('bulkTransactionTable', 'bulk-transaction');
    }

    if (isExportingJSON) {
      downloadJson(transactionData, 'bulk-transaction');
    }

    if (isExportingXML) {
      downloadXml('bulkTransactionTable', 'bulk-transaction');
    }

    if (isPrinting) {
      printFile('bulkTransactionTable');
    }

    setIsExportingPDF(false);
    setIsExportingCSV(false);
    setIsExportingXLS(false);
    setIsExportingDOC(false);
    setIsExportingText(false);
    setIsExportingJSON(false);
    setIsExportingXML(false);
    setIsPrinting(false);
  }, [
    isExportingPDF,
    isExportingCSV,
    isExportingXLS,
    isExportingDOC,
    isExportingText,
    isExportingJSON,
    isExportingXML,
    isPrinting,
  ]);
  const TableProps: vp.TableProps = {
    id: 'bulkTransactionTable',
    name: 'bulk-transaction',
    columns: {
      Id: { header: t('id', { defaultValue: 'Id' }), align: 'left' },
      Name: { header: t('name', { defaultValue: 'Name' }), align: 'left' },
      Account: { header: t('account', { defaultValue: 'Account' }), align: 'left' },
      Message: { header: t('message', { defaultValue: 'Message' }), align: 'left' },
    },
    rows:
      Object.values(transactionData).map((transaction, index) => ({
        Id: String(index + 1),
        Name: String(
          transaction.CompanyName ||
            transaction.BusinessName ||
            transaction.RecipientName ||
            transaction.ReceiverName ||
            [transaction.FirstName, transaction.LastName].filter(Boolean).join(' '),
        ),
        Account: (
          <>
            {transaction.EmailAddress ? transaction.EmailAddress : ''}
            {transaction.CardNumber ? transaction.CardNumber : ''}
            {transaction.CreditCardNumber ? transaction.CreditCardNumber : ''}
            {transaction.FinancialInstitutionNumber ? `${String(transaction.FinancialInstitutionNumber)}-` : ''}
            {transaction.BranchTransitNumber ? `${String(transaction.BranchTransitNumber)}-` : ''}
            {transaction.AccountNumber ? `${String(transaction.AccountNumber)}` : ''}
            {transaction.Token ? `Token: ${String(transaction.Token)}` : ''}
            {transaction.FlinksAccountID ? `Token: ${String(transaction.FlinksAccountID)}` : ''}
            {transaction.FlinksLoginID ? `FlinksLoginID: ${String(transaction.FlinksLoginID)}` : ''}
            {transaction.PlaidPublicToken ? `PlaidPublicToken: ${String(transaction.PlaidPublicToken)}` : ''}
            {transaction.PlaidAccessToken ? `PlaidAccessToken: ${String(transaction.PlaidAccessToken)}` : ''}
            {transaction.PlaidAccountID ? `PlaidAccountID: ${String(transaction.PlaidAccountID)}` : ''}
            {transaction.PlaidProcessorToken ? `PlaidProcessorToken: ${String(transaction.PlaidProcessorToken)}` : ''}
            {transaction.InveriteRequestGUID ? `InveriteRequestGUID: ${String(transaction.InveriteRequestGUID)}` : ''}
          </>
        ),
        Message: transaction.Message ? String(transaction.Message) : '',
      })) || [],
  };
  return (
    <Box flx="col" gap="md">
      <Box jc="sb">
        <Box mb="md" jc="sb">
          <Title title={t('title', { defaultValue: 'Bulk File Summary' })} />
        </Box>
        <Button
          id="back-to-bulk-payment"
          label={t('uploadNewFile', { defaultValue: 'Upload New File' })}
          onClick={() => {
            backToPrev();
          }}
        />
      </Box>
      <Box width="100%" jc="fe" maxheight="36px">
        <div className={styles.dropdownContainer}>
          <Button
            id="export-transactions"
            color="none"
            label={t('export', { defaultValue: 'Export' })}
            height="38px"
            textSize={1}
            background="transparent"
            noShadow
            icon="arrowDown"
            iconLocation="right"
            onClick={() => {
              setIsMenuVisible(true);
            }}
          />
          {isMenuVisible && (
            <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
              <Button
                id="export-PDF-btn"
                label={t('pdf', { defaultValue: 'PDF' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingPDF(true);
                }}
              />
              <Button
                id="export-CSV-btn"
                label={t('csv', { defaultValue: 'CSV' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingCSV(true);
                }}
              />
              <Button
                id="export-XLS-btn"
                label={t('xls', { defaultValue: 'XLS' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingXLS(true);
                }}
              />
              <Button
                id="export-DOC-btn"
                label={t('doc', { defaultValue: 'DOC' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingDOC(true);
                }}
              />
              <Button
                id="export-Text-btn"
                label={t('text', { defaultValue: 'Text' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingText(true);
                }}
              />
              <Button
                id="export-JSON-btn"
                label={t('json', { defaultValue: 'JSON' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingJSON(true);
                }}
              />
              <Button
                id="export-XML-btn"
                label={t('xml', { defaultValue: 'XML' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingXML(true);
                }}
              />
              <Button
                id="export-Print-btn"
                label={t('print', { defaultValue: 'Print' })}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsPrinting(true);
                }}
              />
            </DropDown>
          )}
        </div>
      </Box>
      <div id="bulk-transaction-overview">
        <Table {...TableProps} />
      </div>
    </Box>
  );
};

export default BulkTransactionDetails;
